import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Spinner from './Spinner';

/**
 * Componente para proteger rotas baseado em autenticação e permissões
 * @param {Object} props - Propriedades do componente
 * @param {React.ReactNode} props.children - Componentes filhos a renderizar se autorizado
 * @param {boolean} props.requireAdmin - Se true, exige que o usuário seja administrador
 * @param {string} props.redirectTo - Rota para redirecionamento se não autorizado (padrão: '/dashboard')
 * @param {React.ReactNode} props.fallback - Componente a renderizar durante carregamento
 */
const ProtectedRoute = ({ 
  children, 
  requireAdmin = false, 
  redirectTo = '/dashboard',
  fallback = null 
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" withText text="Verificando permissões..." />
      </div>
    );
  }

  // Verificar se está autenticado
  if (!isAuthenticated || !user) {
    // Salvar a rota atual para redirecionamento após login
    return <Navigate 
      to="/login" 
      state={{ from: location.pathname }} 
      replace 
    />;
  }

  // Verificar se é administrador (quando necessário)
  if (requireAdmin && !user.is_admin) {
    // Usuário autenticado mas não é admin
    return <Navigate 
      to={redirectTo} 
      state={{ 
        from: location.pathname,
        error: 'Acesso negado. Apenas administradores podem acessar esta página.' 
      }} 
      replace 
    />;
  }

  // Usuário autorizado, renderizar children
  return children;
};

export default ProtectedRoute; 